// src/pages/Auth/Login.jsx
import React, { useState } from 'react';
import {
  Container, Paper, Typography, TextField, Button, Box,
  Link, CircularProgress, Divider, Alert, InputAdornment, IconButton,
} from '@mui/material';
import { Login as LoginIcon, Google, Visibility, VisibilityOff } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const Login = () => {
  const [formData,     setFormData]     = useState({ email: '', password: '' });
  const [errors,       setErrors]       = useState({});
  const [loading,      setLoading]      = useState(false);
  const [googleLoading,setGoogleLoading]= useState(false);
  const [loginError,   setLoginError]   = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const navigate        = useNavigate();
  const { refreshUser } = useAuth();

  // ── Standard login ──────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      setErrors({
        email:    !formData.email    ? 'Email required'    : '',
        password: !formData.password ? 'Password required' : '',
      });
      return;
    }
    try {
      setLoading(true);
      setLoginError('');
      const response = await authAPI.login({ email: formData.email, password: formData.password });
      if (response.data.success) {
        const { user, access_token } = response.data;
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(user));
        await refreshUser();
        toast.success('Welcome back! Login successful.');
        redirectByRole(user.user_type);
      } else {
        setLoginError(response.data.message || 'Login failed');
      }
    } catch (error) {
      setLoginError(error.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // ── Redirect helper ─────────────────────────────────────────────────────────
  const redirectByRole = (userType) => {
    if      (userType === 'admin')  navigate('/admin/dashboard');
    else if (userType === 'seller') navigate('/seller/dashboard');
    else if (userType === 'buyer')  navigate('/buyer/dashboard');
    else                            navigate('/auth/select-role');
  };

  // ── Google login (popup) ────────────────────────────────────────────────────
  const handleGoogleLogin = async () => {
    try {
      setGoogleLoading(true);
      setLoginError('');

      const response = await authAPI.googleLogin();
      if (!response.data.success || !response.data.auth_url) {
        throw new Error(response.data.message || 'Failed to get Google login URL');
      }

      const width = 600, height = 700;
      const left  = window.screenX + (window.outerWidth  - width)  / 2;
      const top   = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        response.data.auth_url,
        'Google Login',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      // Popup blocked → fall back to full page redirect
      if (!popup || popup.closed) {
        window.location.href = response.data.auth_url;
        return;
      }

      // ── Listen for postMessage from GoogleCallback inside popup ──
      const handleMessage = async (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== 'GOOGLE_AUTH_REDIRECT') return;

        window.removeEventListener('message', handleMessage);
        if (pollTimer) clearInterval(pollTimer);

        await refreshUser();
        setGoogleLoading(false);

        const storedUser = localStorage.getItem('user');
        const u = storedUser ? JSON.parse(storedUser) : null;
        const { redirectUrl } = event.data;

        // If needs role → go to select-role, else use redirectUrl
        if (!u?.user_type || redirectUrl?.includes('select-role')) {
          navigate('/auth/select-role');
        } else {
          const url = new URL(redirectUrl, window.location.origin);
          navigate(url.pathname + url.search);
        }
      };

      window.addEventListener('message', handleMessage);

      // ── Fallback: popup closed without postMessage ─────────────
      let pollTimer = setInterval(async () => {
        if (!popup.closed) return;
        clearInterval(pollTimer);
        window.removeEventListener('message', handleMessage);

        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (token && storedUser) {
          await refreshUser();
          const u = JSON.parse(storedUser);
          setGoogleLoading(false);
          if (!u?.user_type) {
            navigate('/auth/select-role');
          } else {
            redirectByRole(u.user_type);
          }
        } else {
          setGoogleLoading(false);
        }
      }, 500);

    } catch (error) {
      setLoginError(error.response?.data?.message || error.message || 'Google login failed.');
      setGoogleLoading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LoginIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom>Welcome Back</Typography>
            <Typography variant="body1" color="text.secondary">
              Sign in to your Fruit Export Platform
            </Typography>
          </Box>

          {loginError && <Alert severity="error" sx={{ mb: 3 }}>{loginError}</Alert>}

          <Button fullWidth variant="outlined" size="large"
            startIcon={googleLoading ? <CircularProgress size={20} /> : <Google />}
            onClick={handleGoogleLogin} disabled={googleLoading || loading}
            sx={{ mb: 3, py: 1.5, backgroundColor: '#fff', borderColor: '#ddd',
                  color: '#444', '&:hover': { backgroundColor: '#f8f8f8', borderColor: '#ccc' } }}>
            {googleLoading ? 'Opening Google...' : 'Continue with Google'}
          </Button>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">Or sign in with email</Typography>
          </Divider>

          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="Email Address" name="email" type="email"
              value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!errors.email} helperText={errors.email}
              margin="normal" disabled={loading || googleLoading} autoComplete="email" />

            <TextField fullWidth label="Password" name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              error={!!errors.password} helperText={errors.password}
              margin="normal" disabled={loading || googleLoading} sx={{ mb: 3 }}
              autoComplete="current-password"
              InputProps={{ endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(p => !p)} edge="end" size="small">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )}} />

            <Box sx={{ textAlign: 'right', mb: 3 }}>
              <Link component={RouterLink} to="/forgot-password" variant="body2"
                color="primary" underline="hover">Forgot password?</Link>
            </Box>

            <Button type="submit" fullWidth variant="contained" size="large"
              disabled={loading || googleLoading}
              startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
              sx={{ mb: 3, py: 1.5 }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <Box sx={{ textAlign: 'center', pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <Link component={RouterLink} to="/register" fontWeight="medium"
                  color="primary" underline="hover">Create an account</Link>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;